import { memo, type ReactNode } from 'react'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import type { DataTableProps } from '@/components/data-table/data-table.interface'
import { DataTable } from '@/components/data-table/data-table.organism'
import { cn } from '@/lib/utils'
import { getPromptTemplateRowId } from './prompt-template-table.helper'
import { promptTemplateColumnList } from './prompt-template-table-columns.helper'

type PromptTemplateTableProps = Pick<
  DataTableProps<PromptTemplate>,
  | 'data'
  | 'pagination'
  | 'onPageChange'
  | 'onPageSizeChange'
  | 'searchValue'
  | 'onSearchChange'
  | 'sortBy'
  | 'sortOrder'
  | 'onSortChange'
  | 'actionList'
  | 'toolbarActions'
  | 'isLoading'
  | 'className'
>

/**
 * Table for displaying and managing prompt templates.
 * Wraps the generic DataTable with prompt-template-specific columns and configuration.
 */
const PromptTemplateTableBase: React.FC<PromptTemplateTableProps> = ({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onSearchChange,
  sortBy,
  sortOrder,
  onSortChange,
  actionList,
  toolbarActions,
  isLoading = false,
  className,
}): ReactNode => (
  <DataTable
    data={data}
    columnList={promptTemplateColumnList}
    getRowId={getPromptTemplateRowId}
    pagination={pagination}
    onPageChange={onPageChange}
    onPageSizeChange={onPageSizeChange}
    searchValue={searchValue}
    onSearchChange={onSearchChange}
    searchPlaceholder="Search templates by name or description..."
    sortBy={sortBy}
    sortOrder={sortOrder}
    onSortChange={onSortChange}
    actionList={actionList}
    toolbarActions={toolbarActions}
    isLoading={isLoading}
    emptyMessage="No prompt templates found."
    className={cn('prompt-template-table', className)}
  />
)

export const PromptTemplateTable = memo(PromptTemplateTableBase)

PromptTemplateTable.displayName = 'PromptTemplateTable'
